/**
 * Tiny WebAudio chime used to notify visitors when the assistant or a
 * human agent replies. We synthesize the tone instead of shipping an
 * audio asset so the widget bundle stays small and works offline.
 *
 * Notes:
 *  - AudioContext creation is deferred until the first play call so the
 *    widget never blocks on it (and so we don't trip strict autoplay
 *    policies before the user has interacted with the page).
 *  - We share a single AudioContext per page — re-creating it on each
 *    play leaks resources and causes glitches on iOS.
 *  - Failures (no AudioContext, suspended state, etc.) are swallowed:
 *    a missing chime is never worth breaking the chat over.
 */

type Win = typeof globalThis & {
  AudioContext?: typeof AudioContext;
  webkitAudioContext?: typeof AudioContext;
};

let ctx: AudioContext | null = null;
let lastPlayedAt = 0;

function getContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (ctx) return ctx;
  const w = window as unknown as Win;
  const Ctor = w.AudioContext ?? w.webkitAudioContext;
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
  } catch {
    ctx = null;
  }
  return ctx;
}

/**
 * Play a soft two-note chime (E5 → A5). Throttled to one play per 1.5s
 * to avoid spamming the visitor when several messages arrive back-to-back.
 */
export function playNotificationSound(): void {
  const now = Date.now();
  if (now - lastPlayedAt < 1500) return;
  lastPlayedAt = now;

  const audio = getContext();
  if (!audio) return;

  // Some browsers leave the context suspended until a user gesture; resume
  // is best-effort and harmless if it fails.
  if (audio.state === 'suspended') {
    audio.resume().catch(() => {
      /* swallow */
    });
  }

  const start = audio.currentTime;
  schedule(audio, 659.25, start, 0.18); // E5
  schedule(audio, 880.0, start + 0.12, 0.22); // A5
}

function schedule(
  audio: AudioContext,
  frequency: number,
  startAt: number,
  duration: number,
): void {
  try {
    const osc = audio.createOscillator();
    const gain = audio.createGain();
    osc.type = 'sine';
    osc.frequency.value = frequency;
    // Soft envelope: quick attack, smooth exponential decay.
    gain.gain.setValueAtTime(0.0001, startAt);
    gain.gain.exponentialRampToValueAtTime(0.18, startAt + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);
    osc.connect(gain).connect(audio.destination);
    osc.start(startAt);
    osc.stop(startAt + duration + 0.02);
  } catch {
    /* swallow — audio failures must never break chat */
  }
}
